"use client";

import type {
  CheckoutOptions,
  PaymentCheckout,
  ResumeCheckoutOptions,
} from "./types";

/**
 * Creates a Paystack InlineJS checkout instance.
 *
 * Supports two modes:
 * - Subscription (`planCode`): Paystack overrides the `amount` field when a plan is provided.
 * - One-time charge (`amount`): Pass the amount in subunits (kobo). No planCode.
 *
 * Note: customerCode is intentionally not passed to newTransaction. Returning
 * customers (who have a saved card authorization) use the server-side
 * POST /subscription path (subscribeWithCardOnFile action) instead.
 * Paystack matches new customers to existing accounts by email automatically.
 *
 * @param publicKey - Paystack public key (pk_test_xxx / pk_live_xxx)
 */
export function createPaystackCheckout(publicKey: string): PaymentCheckout {
  return {
    open(options: CheckoutOptions) {
      // biome-ignore lint/suspicious/noExplicitAny: @paystack/inline-js ships no type declarations
      import("@paystack/inline-js" as any).then((mod: any) => {
        const PaystackCtor = mod.default ?? mod;
        // biome-ignore lint/suspicious/noExplicitAny: untyped third-party module
        const popup = new (PaystackCtor as any)();
        popup.newTransaction({
          key: publicKey,
          email: options.email,
          // Subscription: planCode set, amount ignored by Paystack.
          // One-time charge: no planCode, amount in subunits (kobo).
          amount: options.planCode ? 0 : (options.amount ?? 0),
          ...(options.planCode ? { planCode: options.planCode } : {}),
          metadata: options.metadata ?? {},
          onSuccess: (transaction: { reference: string }) => {
            options.onSuccess(transaction.reference);
          },
          onCancel: () => {
            options.onCancel();
          },
          onError: (error: { message: string }) => {
            options.onError(error.message);
          },
        });
      });
    },
    resume(options: ResumeCheckoutOptions) {
      // biome-ignore lint/suspicious/noExplicitAny: @paystack/inline-js ships no type declarations
      import("@paystack/inline-js" as any).then((mod: any) => {
        const PaystackCtor = mod.default ?? mod;
        // biome-ignore lint/suspicious/noExplicitAny: untyped third-party module
        const popup = new (PaystackCtor as any)();
        popup.resumeTransaction(options.accessCode, {
          onSuccess: (transaction: { reference: string }) => {
            options.onSuccess(transaction.reference);
          },
          onCancel: () => {
            options.onCancel();
          },
          onError: (error: { message: string }) => {
            options.onError(error.message);
          },
        });
      });
    },
  };
}
